import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Phone, Mail } from "lucide-react";
import { Seal } from "../components/site/Seal";
import { MaskedLines, FadeUp, Eyebrow } from "../components/site/Reveal";
import { Marquee } from "../components/site/Marquee";
import { ServicesBento } from "../components/home/ServicesBento";
import { WhySection } from "../components/home/WhySection";
import { ClosingCta } from "../components/home/ClosingCta";
import { CONTACT, SERVICES } from "../data/services";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const sealY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const sealR = useTransform(scrollYProgress, [0, 1], [0, 25]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section ref={ref} className="relative min-h-[100svh] bg-[#070F1E] grain overflow-hidden" data-testid="home-hero">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_75%_45%,rgba(27,42,71,0.9),transparent_60%)]" />
      <div className="absolute inset-y-0 left-[58%] hidden lg:block w-px bg-gradient-to-b from-transparent via-[#C5A059]/25 to-transparent" />
      <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 pt-32 sm:pt-40 pb-16 grid lg:grid-cols-12 gap-14 lg:gap-6 items-center min-h-[100svh]">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }}>
            <Eyebrow dark>Civil · Legal · Property Advisory — Guntur</Eyebrow>
          </motion.div>
          <h1 className="mt-8 font-display text-[#F4EEE3] text-[3.4rem] leading-[0.9] sm:text-7xl lg:text-[7.6rem] tracking-[-0.02em]">
            <MaskedLines lines={[
              <>Clear titles.</>,
              <span className="italic text-[#E3C88A]">Secure land.</span>,
              <>Sound counsel.</>,
            ]} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 1 }}
            className="mt-10 max-w-lg text-base sm:text-lg text-[#C9C3B6] leading-relaxed">
            Ridhanya Associates guides individuals, families and corporate entities through property matters, legal documentation and financial securing — with integrity and accuracy.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 1 }} className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/contact" data-testid="hero-enquire-btn" className="group inline-flex items-center gap-4 rounded-full bg-[#C5A059] pl-7 pr-2 py-2 text-[#070F1E] font-medium transition-[background-color] duration-300 hover:bg-[#E3C88A]">
              Book a consultation
              <span className="grid h-10 w-10 place-items-center rounded-full bg-[#070F1E] text-[#E3C88A] transition-transform duration-500 group-hover:rotate-45"><ArrowUpRight className="h-4 w-4" /></span>
            </Link>
            <Link to="/services" data-testid="hero-services-btn" className="inline-flex items-center gap-2 px-4 py-3 text-[#F4EEE3] underline-offset-8 decoration-[#C5A059] hover:underline">Explore services</Link>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 1 }} className="mt-14 flex flex-col sm:flex-row gap-4 sm:gap-10 text-sm text-[#C9C3B6]">
            <a href={`tel:${CONTACT.tel}`} className="flex items-center gap-2 hover:text-[#E3C88A]" data-testid="hero-phone-link"><Phone className="h-4 w-4 text-[#C5A059]" />{CONTACT.phone}</a>
            <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-2 hover:text-[#E3C88A] break-all" data-testid="hero-email-link"><Mail className="h-4 w-4 text-[#C5A059]" />{CONTACT.email}</a>
          </motion.div>
        </motion.div>
        <motion.div style={{ y: sealY, rotate: sealR }} className="lg:col-span-5 flex justify-center lg:justify-end">
          <Seal size="w-[72vw] max-w-[480px]" />
        </motion.div>
      </div>
    </section>
  );
};

const Overview = () => (
  <section className="paper py-24 sm:py-36" data-testid="home-overview">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-3"><Eyebrow>Overview</Eyebrow></div>
      <div className="lg:col-span-9">
        <h2 className="font-display text-[#0A192F] text-3xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight">
          <MaskedLines inView delay={0} lines={["A premier consultancy for", <span className="italic text-[#8A6A2B]">civil, legal & property</span>, "matters — under one roof."]} />
        </h2>
        <FadeUp delay={0.2} className="mt-12 grid sm:grid-cols-2 gap-8 text-[#2C3545] text-base sm:text-lg leading-relaxed">
          <p>With a commitment to integrity, accuracy, and client satisfaction, we assist individuals, families, and corporate entities in navigating complex property matters.</p>
          <p>From legal documentation to financial securing, every engagement is handled with clarity, diligence and a single point of contact.</p>
        </FadeUp>
        <FadeUp delay={0.3}><Link to="/about" data-testid="overview-about-link" className="mt-10 inline-flex items-center gap-2 text-[#0A192F] border-b border-[#8A6A2B] pb-1 hover:gap-4 transition-[gap] duration-300">About the firm <ArrowUpRight className="h-4 w-4" /></Link></FadeUp>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee items={SERVICES.map((s) => s.title)} dark />
      <Overview />
      <ServicesBento />
      <WhySection />
      <ClosingCta />
    </>
  );
}
